import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState, useEffect, forwardRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { faSearch, faCalendar } from "@fortawesome/free-solid-svg-icons";
import msexcel from "../../assets/image/msexcel.png";
import moment from "moment/moment";
import DatePicker, { CalendarContainer } from "react-datepicker";

const CustomInput = forwardRef((props, ref) => (
  <button {...props}>{props.value}</button>
));

export default function Datatable({
  columns,
  data = [],
  dateFieldForFilter,
  onTableDataChange,
  filterName = "title",
  conditionalRowStyles,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
}) {
  const [tableData, setTableData] = useState(data);

  const [showAll, setShowAll] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [loading, setLoading] = useState(true);

  const [dateRange, setDateRange] = useState([null, null]);
  const [fromDate, toDate] = dateRange;

  const handleViewAll = () => {
    setShowAll((prev) => !prev);
  };
  const fetchData = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setLoading(false);
  };
  useEffect(() => {
    fetchData();
  }, [loading]);

  function convertArrayOfObjectsToCSV(array) {
    let result;
    const columnDelimiter = ",";
    const lineDelimiter = "\n";
    const keys = Object.keys(array[0]);

    result = "";
    // result += keys.join(columnDelimiter);
    result += keys
      .map((key) => {
        key = key.replace(/([A-Z])/g, " $1");
        return key.charAt(0).toUpperCase() + key.slice(1);
      })
      .join(columnDelimiter);

    result += lineDelimiter;

    array.forEach((item) => {
      let ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) result += columnDelimiter;
        result += item[key];
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function downloadCSV(array) {
    const link = document.createElement("a");
    let csv = convertArrayOfObjectsToCSV(array);
    if (csv == null) return;

    const filename = "export.csv";

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", filename);
    link.click();
  }

  const Export = ({ onExport }) => (
    <Button
      onClick={(e) => onExport(e.target.value)}
      variant="outline-secondary"
      className="btn_export"
    >
      <img src={msexcel} alt="" className="me-1" />
      Export
    </Button>
  );

  const actionsMemo = React.useMemo(
    () => <Export onExport={() => downloadCSV(tableData)} />,
    [downloadCSV, tableData]
  );

  const paginationComponentOptions = {
    selectAllRowsItem: true,
    selectAllRowsItemText: "All",
  };

  const ExpandedComponent = ({ data }) => (
    <pre>{JSON.stringify(data, null, 2)}</pre>
  );

  // Handles filters
  useEffect(() => {
    if (showAll) {
      setTableData(data);
    }

    let tempData = data;

    if (searchKey.length) {
      tempData = tempData.filter((item) =>
        JSON.stringify(item)?.toLowerCase().includes(searchKey?.toLowerCase())
      );
    }

    // const from = moment(fromDate);
    // const to = moment(toDate);
    // if (from.isValid() && to.isValid()) {
    //   tempData = tempData.filter((item) => {
    //     const curr = moment(item?.[dateFieldForFilter]);
    //     return curr.isAfter(from) && curr.isBefore(to);
    //   });
    // }
    const from = fromDate ? moment(fromDate).startOf("day") : null;
    const to = toDate ? moment(toDate).endOf("day") : null;

    if (from && to) {
      tempData = tempData.filter((item) => {
        const curr = moment(item?.[dateFieldForFilter]);
        return curr.isSameOrAfter(from) && curr.isSameOrBefore(to);
      });
    } else if (from) {
      tempData = tempData.filter((item) => {
        const curr = moment(item?.[dateFieldForFilter]);
        return curr.isSameOrAfter(from);
      });
    } else if (to) {
      tempData = tempData.filter((item) => {
        const curr = moment(item?.[dateFieldForFilter]);
        return curr.isSameOrBefore(to);
      });
    }

    setTableData(tempData);
  }, [data, searchKey, showAll, fromDate, toDate]);

  // pushes filtered data to parent component
  useEffect(() => {
    if (onTableDataChange) onTableDataChange(tableData);
  }, [tableData]);

  return (
    <div className="table-container">
      <div className="tableHeader">
        <div className="fieldArea d-flex">
          {dateFieldForFilter && (
            <div className="date-range-container position-relative">
              <FontAwesomeIcon icon={faCalendar} />
              <DatePicker
                className="me-2 filter-date-picker"
                placeholderText="Select Date Range"
                popperPlacement="bottom-start"
                maxDate={new Date()}
                selectsRange={true}
                startDate={fromDate}
                endDate={toDate}
                customInput={<CustomInput style={{ minWidth: "260px" }} />}
                onChange={(update) => {
                  setDateRange(update);
                }}
                isClearable
              />
            </div>
          )}
          <div className="search-container position-relative me-2">
            <input
              type="text"
              placeholder="Search"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
            />
            <FontAwesomeIcon icon={faSearch} />
          </div>
          <div>{actionsMemo}</div>
        </div>
        <Button onClick={handleViewAll} className="viewAll">
          View All
        </Button>
      </div>
      <div className="datatable-container">
        {loading ? (
          <div className="loader-container d-flex justify-content-center align-items-center">
            <div className="me-2">
              <Spinner animation="border" variant="success" />
            </div>
            <div className="spin_load">
              <span className="d-flex">
                Loading
                <span className="dot-one"> .</span>
                <span className="dot-two"> .</span>
                <span className="dot-three"> .</span>
              </span>
            </div>
          </div>
        ) : (
          // : tableData.length === 0 ? (
          //   <div className="no-records-container">
          //     <p>There are no records to display</p>
          //   </div>
          // )
          <DataTable
            key={showAll ? "showAll" : "filtered"}
            fixedHeader
            columns={columns}
            data={tableData}
            pagination
            expandableRowsComponent={ExpandedComponent}
            defaultSortAsc={false}
            defaultSortFieldId="Date"
            actions={actionsMemo}
            paginationComponentOptions={paginationComponentOptions}
            conditionalRowStyles={conditionalRowStyles}
          />
        )}
      </div>
    </div>
  );
}
